import { useTheme } from "@mui/material/styles";
import {
  LucideCaptionsOff,
  LucideMaximize,
  LucideMinimize,
  LucidePause,
  LucidePictureInPicture2,
  LucidePlay,
  LucideSubtitles,
  LucideVolume2,
  LucideVolumeX
} from "lucide-react";
import { PointerEvent as ReactPointerEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  BottomControlsBar,
  CaptionsControlWrapper,
  CaptionsMenu,
  CaptionsMenuItem,
  CenterButton,
  ControlsGroupLeft,
  ControlsGroupRight,
  ControlsOverlay,
  ControlsRow,
  ProgressBarFill,
  ProgressBarHitArea,
  ProgressBarThumb,
  ProgressBarTrack,
  SmallControlButton,
  TimestampText
} from "./PlayerControls.styles";
import type { BrightcovePlayer } from "./PlayerLoader.types";

interface PlayerControlsProps {
  player: BrightcovePlayer | null
  isRestricted: boolean
  progressUnlocked: boolean
  hidePlayButton: boolean
  isFullScreen?: boolean
}

function formatTime(totalSeconds: number) {
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return "00:00";
  const flooredSeconds = Math.floor(totalSeconds);
  const hours = Math.floor(flooredSeconds / 3600);
  const minutes = Math.floor((flooredSeconds % 3600) / 60);
  const seconds = flooredSeconds % 60;
  const mm = String(minutes).padStart(2, "0");
  const ss = String(seconds).padStart(2, "0");

  return hours > 0 ? `${String(hours).padStart(2, "0")}:${mm}:${ss}` : `${mm}:${ss}`;
}

export function PlayerControls({
  player,
  isRestricted,
  progressUnlocked,
  hidePlayButton,
  isFullScreen
}: PlayerControlsProps) {
  const theme = useTheme();
  const { t } = useTranslation();

  const [isPaused, setIsPaused] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragTime, setDragTime] = useState(0);
  const [pipSupported, setPipSupported] = useState(false);
  const [subtitleTracks, setSubtitleTracks] = useState<TextTrack[]>([]);
  const [captionsMenuOpen, setCaptionsMenuOpen] = useState(false);

  const trackRef = useRef<HTMLDivElement>(null);
  const captionsWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!player) return;

    const handleTimeUpdate = () => {
      setCurrentTime(player.currentTime());
      setDuration(player.duration());
    };
    const handlePlay = () => setIsPaused(false);
    const handlePause = () => setIsPaused(true);
    const handleVolumeChange = () => {
      setIsMuted(player.muted());
      setVolume(player.volume());
    };

    player.on("timeupdate", handleTimeUpdate);
    player.on("play", handlePlay);
    player.on("playing", handlePlay);
    player.on("pause", handlePause);
    player.on("volumechange", handleVolumeChange);

    handleTimeUpdate();
    setIsPaused(player.paused());
    handleVolumeChange();

    const videoEl = player.el()?.querySelector("video");
    setPipSupported(!!videoEl && typeof videoEl.requestPictureInPicture === "function");

    const playerTextTracks = player.textTracks();

    const updateCaptionsState = () => {
      const tracks = Array.from(playerTextTracks).filter(
        (track) => track.kind === "subtitles" || track.kind === "captions"
      );
      setSubtitleTracks(tracks);
    };

    updateCaptionsState();
    playerTextTracks.addEventListener("addtrack", updateCaptionsState);
    playerTextTracks.addEventListener("removetrack", updateCaptionsState);

    return () => {
      playerTextTracks.removeEventListener("addtrack", updateCaptionsState);
      playerTextTracks.removeEventListener("removetrack", updateCaptionsState);
    };
  }, [player]);

  useEffect(() => {
    if (!captionsMenuOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (!captionsWrapperRef.current?.contains(e.target as Node)) {
        setCaptionsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [captionsMenuOpen]);

  
  if (!player) return null;
  
  // Calcolato ad ogni render (non in state) perché "subtitleTracks" contiene riferimenti
  // live ai TextTrack: Brightcove può popolare/aggiornare "language" in modo asincrono,
  // senza generare un evento "addtrack"/"removetrack" che ce lo segnali.
  // Confronto per riferimento all'oggetto TextTrack (non per "language"): Brightcove a volte
  // lascia "language" vuoto/inconsistente pur mostrando un'etichetta valida (track.label).
  const activeCaptionsTrack = subtitleTracks.find((track) => track.mode === "showing") ?? null;

  const displayedCurrentTime = isDragging ? dragTime : currentTime;
  const progressPercent = duration > 0 ? (displayedCurrentTime / duration) * 100 : 0;
  const isSeekDisabled = isRestricted && !progressUnlocked;

  const seekFromClientX = (clientX: number) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0 || duration <= 0) return;

    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const newTime = ratio * duration;
    setDragTime(newTime);
    player.currentTime(newTime);
  };

  const handleTrackPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (isSeekDisabled) return;
    setIsDragging(true);
    seekFromClientX(e.clientX);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handleTrackPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!isDragging || isSeekDisabled) return;
    seekFromClientX(e.clientX);
  };

  const handleTrackPointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    setIsDragging(false);
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  };

  const togglePlayPause = () => {
    if (player.paused()) {
      player.play();
    } else {
      player.pause();
    }
  };

  const toggleMute = () => {
    player.muted(!player.muted());
  };

  const togglePictureInPicture = () => {
    const videoEl = player.el()?.querySelector("video") as HTMLVideoElement | null;
    if (!videoEl) return;

    try {
      if (document.pictureInPictureElement === videoEl) {
        document.exitPictureInPicture();
      } else {
        videoEl.requestPictureInPicture();
      }
    } catch {
      // Picture-in-Picture non disponibile in questo stato/browser: nessuna azione.
    }
  };

  const selectCaptionsTrack = (selectedTrack: TextTrack | null) => {
    subtitleTracks.forEach((track) => {
      track.mode = track === selectedTrack ? "showing" : "disabled";
    });
    setCaptionsMenuOpen(false);
  };

  const toggleFullscreen = () => {
    if (isFullScreen) {
      player.exitFullscreen();
    } else {
      player.requestFullscreen();
    }
  };

  const isMutedOrSilent = isMuted || volume === 0;

  return (
    <ControlsOverlay>
      { !hidePlayButton && (
        <CenterButton
          aria-label={ t("player_play") }
          onClick={ togglePlayPause }
          type="button"
        >
          { isPaused ? (
            <LucidePlay color={ theme.customColors.textPrimary } size={ 30 } />
          ) : (
            <LucidePause color={ theme.customColors.textPrimary } size={ 30 } />
          ) }
        </CenterButton>
      ) }

      <BottomControlsBar>
        <ProgressBarHitArea
          onPointerDown={ handleTrackPointerDown }
          onPointerMove={ handleTrackPointerMove }
          onPointerUp={ handleTrackPointerUp }
        >
          <ProgressBarTrack $disabled={ isSeekDisabled } ref={ trackRef }>
            <ProgressBarFill $value={ progressPercent } />
            <ProgressBarThumb $value={ progressPercent } />
          </ProgressBarTrack>
        </ProgressBarHitArea>

        <ControlsRow>
          <ControlsGroupLeft>
            <SmallControlButton
              aria-label={ t("player_toggle_mute") }
              onClick={ toggleMute }
              type="button"
            >
              { isMutedOrSilent ? (
                <LucideVolumeX color={ theme.customColors.textPrimary } size={ "1.25rem" } />
              ) : (
                <LucideVolume2 color={ theme.customColors.textPrimary } size={ "1.25rem" } />
              ) }
            </SmallControlButton>
            <TimestampText>
              { formatTime(displayedCurrentTime) } / { formatTime(duration) }
            </TimestampText>
          </ControlsGroupLeft>

          <ControlsGroupRight>
            { subtitleTracks.length > 0 && (
              <CaptionsControlWrapper ref={ captionsWrapperRef }>
                { captionsMenuOpen && (
                  <CaptionsMenu>
                    <CaptionsMenuItem
                      $active={ activeCaptionsTrack === null }
                      onClick={ () => selectCaptionsTrack(null) }
                      type="button"
                    >
                      { t("player_subtitles_off") }
                    </CaptionsMenuItem>
                    { subtitleTracks.map((track) => (
                      <CaptionsMenuItem
                        $active={ activeCaptionsTrack?.id === track?.id }
                        key={ track.language || track.label }
                        onClick={ () => selectCaptionsTrack(track) }
                        type="button"
                      >
                        { track.label || track.language }
                      </CaptionsMenuItem>
                    )) }
                  </CaptionsMenu>
                ) }
                <SmallControlButton
                  aria-expanded={ captionsMenuOpen }
                  aria-label={ t("player_subtitles") }
                  onClick={ () => setCaptionsMenuOpen((open) => !open) }
                  type="button"
                >
                  { activeCaptionsTrack ? (
                    <LucideSubtitles color={ theme.customColors.textPrimary } size={ 20 } />
                  ) : (
                    <LucideCaptionsOff color={ theme.customColors.textPrimary } size={ 20 } />
                  ) }
                </SmallControlButton>
              </CaptionsControlWrapper>
            ) }
            { pipSupported && (
              <SmallControlButton
                aria-label={ t("player_picture_in_picture") }
                onClick={ togglePictureInPicture }
                type="button"
              >
                <LucidePictureInPicture2 color={ theme.customColors.textPrimary } size={ 20 } />
              </SmallControlButton>
            ) }
            <SmallControlButton
              aria-label={ t("player_fullscreen_control") }
              onClick={ toggleFullscreen }
              type="button"
            >
              { isFullScreen ? (
                <LucideMinimize color={ theme.customColors.textPrimary } size={ 20 } />
              ) : (
                <LucideMaximize color={ theme.customColors.textPrimary } size={ 20 } />
              ) }
            </SmallControlButton>
          </ControlsGroupRight>
        </ControlsRow>
      </BottomControlsBar>
    </ControlsOverlay>
  );
}
